import React, { useState, useEffect } from "react";
import { Button, Input, Space, Card, Typography, message, Grid } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import NoteRulesEmptyState from "./NoteRulesEmptyState";
import NoteRulesExamples from "./NoteRulesExamples";
import NoteRulesList from "./NoteRulesList";
import fetchNoteRulesList from "../../utils/preferences/fetchNoteRulesList";
import createNoteRule from "../../utils/preferences/createNoteRule";
import { useTranslation } from "react-i18next";
import { toast } from 'react-hot-toast';
import { logEvent } from '../../analytics';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;


export default function NoteRules() {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [rules, setRules] = useState([]);
  const [newRuleText, setNewRuleText] = useState("");
  const [loading, setLoading] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRules();
  }, [pagination.current]);

  const loadRules = async () => {
    try {
      setLoading(true);
      const { rules: fetchedRules, pagination: fetchedPagination } = await fetchNoteRulesList(pagination.current);
      setRules(fetchedRules);
      setPagination(fetchedPagination);
    } catch (error) {
      message.error('Failed to load note rules');
      setError('Failed to load note rules');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (newRuleText.trim()) {
      try {
        const newRule = await createNoteRule(newRuleText.trim());
        setRules([...rules, newRule]);
        setNewRuleText("");
        setIsAddingNew(false);
        toast.success(t('common.saved'));
        logEvent('NoteRules', 'Add');
        loadRules(); // Reload to get updated pagination
      } catch (error) {
        toast.error(t('note_rules.errors.save'));
      }
    }
  };

  const handleCancelAdd = () => {
    setNewRuleText("");
    setIsAddingNew(false);
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handleRulesChange = (newRules) => {
    setRules(newRules);
  };

  return (
    <div>
      <Paragraph style={{ display: "block" }}>
        {t('note_rules.subtitle')}
      </Paragraph>
      {screens.sm && <Text style={{ display: "block", fontStyle: "italic" }}>
        {t('note_rules.notice')}
      </Text>}

      {error && (
        <div data-testid="error-message" style={{ marginBottom: "16px" }}>
          <Text type="danger">{error}</Text>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "26px", marginTop: "25px" }}>
        {!isAddingNew ? (
          <Button
            data-testid="add-rule-button"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddingNew(true)}
          >
            {t('note_rules.add_rule')}
          </Button>
        ) : (
          <Card 
            data-testid="add-rule-form"
            style={{ 
              width: "100%", 
              marginBottom: "16px", 
              marginTop: "26px",
              border: "1px solid #e8e8e8",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              backgroundColor: "#ffffff"
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text style={{ display: "block", marginBottom: "16px", fontSize: '16px' }}>
                {t('note_rules.add_rule_description')}
              </Text>
              <TextArea
                data-testid="add-rule-textarea"
                placeholder={t('note_rules.add_rule_placeholder')}
                value={newRuleText}
                onChange={(e) => setNewRuleText(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 6 }}
                style={{ backgroundColor: '#f5f5f5', padding: '16px', fontSize: '16px' }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "10px" }}>
                <Button 
                  data-testid="cancel-new-rule-button"
                  onClick={handleCancelAdd}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  data-testid="save-new-rule-button"
                  type="primary" 
                  onClick={handleAddRule}
                >
                  {t('common.save')}
                </Button>
              </div>
            </Space>
          </Card>
        )}
      </div>

      {loading ? (
        <Card loading />
      ) : rules.length === 0 && !isAddingNew ? (
        <NoteRulesEmptyState data-testid="empty-state" onAddRule={() => setIsAddingNew(true)} />
      ) : !isAddingNew ? (
        <NoteRulesList
          rules={rules}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRulesChange={handleRulesChange}
        />
      ) : null}

      {!loading && (
        <div style={{ marginTop: "50px" }}>
          <NoteRulesExamples data-testid="note-rules-examples" />
        </div>
      )}
    </div>
  );
} 